import { defineComponent as _defineComponent } from 'vue'
import { createElementVNode as _createElementVNode, openBlock as _openBlock, createElementBlock as _createElementBlock, createCommentVNode as _createCommentVNode, renderList as _renderList, Fragment as _Fragment, toDisplayString as _toDisplayString, unref as _unref, normalizeClass as _normalizeClass, createBlock as _createBlock } from "vue"

const _hoisted_1 = ["aria-busy"]
const _hoisted_2 = {
  key: 0,
  class: "resident-notifications__empty"
}
const _hoisted_3 = {
  key: 1,
  class: "resident-notifications__list"
}
const _hoisted_4 = ["data-notification-id", "data-notification-title", "data-notification-date", "data-notification-description"]
const _hoisted_5 = { class: "notification-item__left-container" }
const _hoisted_6 = { class: "notification-item__left-container--title" }
const _hoisted_7 = { class: "notification-item__left-container--description" }
const _hoisted_8 = { class: "notification-item__right-container" }
const _hoisted_9 = { class: "notification-item__right-container--date" }
const _hoisted_10 = { class: "notification-item__right-container--status-container" }
const _hoisted_11 = { class: "notification-item__right-container--status" }

import { nextTick, onBeforeUnmount, onMounted, reactive, ref } from 'vue';
import { formatMatchCountSummary } from '@/js/utils/search-highlight';
import {
  applyServerSearchResults,
  initSearchInput,
  querySearchInput,
  type SearchHighlightConfig,
} from '@/js/utils/search-input';
import { updateNotificationsSidebarBadge } from '@/js/utils/notifications-sidebar-badge';
import Pagination from '../Pagination.vue';
import {
  fetchNotificationList,
  NOTIFICATIONS_PAGE_PARAM,
  RESIDENT_LK_NOTIFICATIONS_TAB_ID,
} from './notification-api';
import {
  getNotificationStatusLabel,
  mapNotificationToCard,
  type NotificationCardItem,
} from './map-notification';

const CHANGED_EVENT = 'residentNotifications:changed';
const SEARCH_INPUT_ID = 'residentNotificationsSearch';
const SEARCH_MIN_LENGTH = 3;


export default /*@__PURE__*/_defineComponent({
  __name: 'index',
  setup(__props) {

const NOTIFICATIONS_SEARCH_HIGHLIGHT: SearchHighlightConfig = {
  cardSelector: '.notification-item',
  fields: [
    { selector: '.notification-item__left-container--title' },
    { selector: '.notification-item__left-container--description' },
  ],
};

const loading = ref(false);
const items = ref<NotificationCardItem[]>([]);
const searchQuery = ref('');
const currentPage = ref(1);
const nav = reactive({
  totalCount: 0,
  pageSize: 20,
  currentPage: 1,
  pageCount: 1,
});

let cleanupSearchInput: (() => void) | null = null;
let cleanupNotificationsReload: (() => void) | null = null;
let searchDebounceTimer: ReturnType<typeof setTimeout> | undefined;
let lastSubmittedSearch = '';
let searchReady = false;

onMounted(() => {
  syncSearchFromUrl();
  bindSearchInput();
  bindNotificationsReload();
  void loadNotifications();
  queueMicrotask(() => {
    searchReady = true;
    lastSubmittedSearch = searchQuery.value;
  });
});

onBeforeUnmount(() => {
  cleanupSearchInput?.();
  cleanupNotificationsReload?.();
  if (searchDebounceTimer) {
    clearTimeout(searchDebounceTimer);
  }
});

function syncSearchFromUrl(): void {
  const params = new URLSearchParams(window.location.search);
  searchQuery.value = params.get('q') ?? '';
  currentPage.value = parsePageFromUrl(params);
}

function parsePageFromUrl(params: URLSearchParams): number {
  const pageParam = params.get(NOTIFICATIONS_PAGE_PARAM) ?? params.get('notifications-page');
  const match = pageParam?.match(/^page-(\d+)$/);
  return match ? Math.max(1, Number(match[1])) : 1;
}

function shouldSubmitSearch(value: string): boolean {
  const trimmed = value.trim();

  if (!trimmed) {
    return true;
  }

  if (trimmed.length >= SEARCH_MIN_LENGTH) {
    return true;
  }

  return lastSubmittedSearch.trim().length >= SEARCH_MIN_LENGTH;
}

function syncFiltersVisibility(): void {
  const root = document.querySelector<HTMLElement>('[data-resident-notifications]');
  if (!root) return;

  const isCompletelyEmpty =
    !loading.value && nav.totalCount === 0 && !searchQuery.value.trim();

  root.classList.toggle('resident-notifications--empty', isCompletelyEmpty);
}

function updateSearchSummary(): void {
  const summary = querySearchInput(document, SEARCH_INPUT_ID)?.summary;
  if (!summary) return;

  const query = searchQuery.value.trim();
  if (!query || query.length < SEARCH_MIN_LENGTH) {
    summary.hidden = true;
    summary.innerHTML = '';
    return;
  }

  summary.hidden = false;
  summary.innerHTML = formatMatchCountSummary(nav.totalCount, query);
}

async function syncSearchHighlight(): Promise<void> {
  await nextTick();

  const query = searchQuery.value.trim();
  if (!query || query.length < SEARCH_MIN_LENGTH) {
    applyServerSearchResults(document, '', {
      inputId: SEARCH_INPUT_ID,
      highlight: NOTIFICATIONS_SEARCH_HIGHLIGHT,
    });
    updateSearchSummary();
    return;
  }

  applyServerSearchResults(document, query, {
    inputId: SEARCH_INPUT_ID,
    highlight: NOTIFICATIONS_SEARCH_HIGHLIGHT,
  });
  updateSearchSummary();
}

function bindSearchInput(): void {
  const elements = initSearchInput(document, {
    inputId: SEARCH_INPUT_ID,
    mode: 'server',
    urlParam: 'q',
  });
  if (!elements) {
    return;
  }

  elements.input.value = searchQuery.value;

  const scheduleSearch = (): void => {
    if (!searchReady) {
      return;
    }

    searchQuery.value = elements.input.value;

    if (!shouldSubmitSearch(searchQuery.value)) {
      if (searchQuery.value.trim().length < SEARCH_MIN_LENGTH) {
        void syncSearchHighlight();
      }
      return;
    }

    if (searchDebounceTimer) {
      clearTimeout(searchDebounceTimer);
    }

    searchDebounceTimer = setTimeout(() => {
      lastSubmittedSearch = searchQuery.value;
      currentPage.value = 1;
      syncUrlToBrowser();
      void loadNotifications();
    }, 400);
  };

  const submitSearchImmediately = (): void => {
    if (!searchReady) {
      return;
    }

    searchQuery.value = elements.input.value;
    lastSubmittedSearch = searchQuery.value;
    currentPage.value = 1;
    syncUrlToBrowser();
    void loadNotifications();
  };

  const handleClear = (): void => {
    queueMicrotask(submitSearchImmediately);
  };

  elements.input.addEventListener('input', scheduleSearch);
  elements.clearBtn.addEventListener('click', handleClear);

  cleanupSearchInput = () => {
    elements.input.removeEventListener('input', scheduleSearch);
    elements.clearBtn.removeEventListener('click', handleClear);
    cleanupSearchInput = null;
  };
}

function bindNotificationsReload(): void {
  const reloadNotifications = (): void => {
    void loadNotifications();
  };

  window.addEventListener(CHANGED_EVENT, reloadNotifications);
  cleanupNotificationsReload = () => {
    window.removeEventListener(CHANGED_EVENT, reloadNotifications);
    cleanupNotificationsReload = null;
  };
}

function syncUrlToBrowser(): void {
  const url = new URL(window.location.pathname, window.location.origin);
  url.searchParams.set('tab', String(RESIDENT_LK_NOTIFICATIONS_TAB_ID));

  const query = searchQuery.value.trim();
  if (query) {
    url.searchParams.set('q', query);
  }

  if (currentPage.value > 1) {
    url.searchParams.set(NOTIFICATIONS_PAGE_PARAM, `page-${currentPage.value}`);
  }

  window.history.replaceState({ tab: RESIDENT_LK_NOTIFICATIONS_TAB_ID }, '', url.toString());
}

async function loadNotifications(): Promise<void> {
  loading.value = true;

  try {
    const data = await fetchNotificationList({
      q: searchQuery.value,
      page: currentPage.value,
    });

    items.value = data.messages.map(mapNotificationToCard);
    nav.totalCount = data.nav.totalCount;
    nav.pageSize = data.nav.pageSize;
    nav.currentPage = data.nav.currentPage;
    nav.pageCount = data.nav.pageCount;
    currentPage.value = data.nav.currentPage;

    updateNotificationsSidebarBadge(document, data.countNewMessages);
  } catch (error) {
    console.error(error);
    items.value = [];
    nav.totalCount = 0;
    nav.pageCount = 1;
  } finally {
    loading.value = false;
    syncFiltersVisibility();
    await syncSearchHighlight();
  }
}

function handlePageChange(page: number): void {
  currentPage.value = page;
  syncUrlToBrowser();
  void loadNotifications();
}

return (_ctx: any,_cache: any) => {
  return (_openBlock(), _createElementBlock("div", {
    class: _normalizeClass(["resident-notifications__results", { 'is-loading': loading.value }]),
    "data-resident-notifications-results": "",
    "aria-busy": loading.value ? 'true' : undefined
  }, [
    (!loading.value && !items.value.length && !searchQuery.value.trim())
      ? (_openBlock(), _createElementBlock("div", _hoisted_2, [...(_cache[1] || (_cache[1] = [
          _createElementVNode("h3", { class: "resident-notifications__empty-title" }, "Нет уведомлений", -1),
          _createElementVNode("p", { class: "resident-notifications__empty-text" }, "Они появятся, загляните позже :)", -1)
        ]))]))
      : (items.value.length)
        ? (_openBlock(), _createElementBlock("div", _hoisted_3, [
            (_openBlock(true), _createElementBlock(_Fragment, null, _renderList(items.value, (item) => {
              return (_openBlock(), _createElementBlock("div", {
                key: item.id,
                class: _normalizeClass(["notification-item", {
          'notification-item--new': item.isNew,
        }]),
                role: "button",
                tabindex: "0",
                "data-notification-open-drawer": "",
                "data-notification-id": item.id,
                "data-notification-title": item.title,
                "data-notification-date": item.date,
                "data-notification-description": item.description
              }, [
                _createElementVNode("div", _hoisted_5, [
                  _createElementVNode("span", _hoisted_6, _toDisplayString(item.title), 1),
                  _createElementVNode("span", _hoisted_7, _toDisplayString(item.description), 1)
                ]),
                _createElementVNode("div", _hoisted_8, [
                  _createElementVNode("span", _hoisted_9, _toDisplayString(item.date), 1),
                  _createElementVNode("div", _hoisted_10, [
                    _cache[2] || (_cache[2] = _createElementVNode("span", { class: "notification-item__right-container--status-dot" }, null, -1)),
                    _createElementVNode("span", _hoisted_11, _toDisplayString(_unref(getNotificationStatusLabel)(item.statusCode)), 1)
                  ])
                ])
              ], 10, _hoisted_4))
            }), 128))
          ]))
        : _createCommentVNode("", true),
    (nav.totalCount > nav.pageSize)
      ? (_openBlock(), _createBlock(Pagination, {
          key: 2,
          modelValue: currentPage.value,
          "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((currentPage).value = $event)),
          class: "resident-notifications__pagination",
          "total-count": nav.totalCount,
          "page-size": nav.pageSize,
          onChange: handlePageChange
        }, null, 8, ["modelValue", "total-count", "page-size"]))
      : _createCommentVNode("", true)
  ], 10, _hoisted_1))
}
}

})